/**
 * API Utility Module
 * Provides robust API calls with:
 * - Configurable timeouts
 * - Automatic retry for transient failures
 * - Rate limit handling
 * - Error handling and logging
 * - Request cancellation
 */

/**
 * Runtime API URL detection.
 * - Production (elido.nu): uses relative URLs (same-origin) — no .env needed
 * - Preview/Dev: uses REACT_APP_BACKEND_URL from .env
 * This eliminates the recurring issue of .env resets breaking production deploys.
 */
function resolveApiUrl() {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'elido.nu' || hostname === 'www.elido.nu') {
      return '';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
}

export const API_URL = resolveApiUrl();

// Default configuration
const DEFAULT_TIMEOUT = 30000; // 30 seconds
const DEFAULT_RETRY_COUNT = 2;
const DEFAULT_RETRY_DELAY = 1000; // 1 second

// Rate limit error messages (Swedish)
const RATE_LIMIT_MESSAGES = {
  default: "Du har gjort för många förfrågningar. Vänta en stund och försök igen.",
  demo: "Du har redan skapat max antal demos. Vänta en stund och försök igen.",
  email: "Du har redan skickat max antal e-post. Vänta en stund och försök igen.",
  cooldown: "Vänta en stund innan du försöker igen."
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Create a timeout promise that rejects after specified ms
 */
const createTimeoutPromise = (ms, controller) => {
  return new Promise((_, reject) => {
    setTimeout(() => {
      controller?.abort();
      reject(new Error(`Request timeout after ${ms}ms`));
    }, ms);
  });
};

/**
 * Determine if an error is retryable
 */
const isRetryableError = (error, response) => {
  // Network errors are retryable
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    return true;
  }
  
  // Timeout errors are retryable
  if (error.message?.includes('timeout')) {
    return true;
  }
  
  // 5xx server errors are retryable
  if (response && response.status >= 500) {
    return true;
  }
  
  // 429 rate limiting is retryable
  if (response && response.status === 429) {
    return true;
  }
  
  return false;
};

/**
 * Log API errors (non-sensitive)
 */
const logApiError = (endpoint, method, error, attempt) => {
  console.error(`[API Error] ${method} ${endpoint}`, {
    attempt,
    errorType: error.name,
    message: error.message,
    timestamp: new Date().toISOString()
  });
};

/**
 * Enhanced fetch with timeout, retry, and error handling
 * 
 * @param {string} endpoint - API endpoint (will be prefixed with API_URL)
 * @param {object} options - Fetch options
 * @param {object} config - Configuration options
 * @returns {Promise<Response>}
 */
export async function apiFetch(endpoint, options = {}, config = {}) {
  const {
    timeout = DEFAULT_TIMEOUT,
    retries = DEFAULT_RETRY_COUNT,
    retryDelay = DEFAULT_RETRY_DELAY,
    retryOn = ['network', 'timeout', '5xx'],
    onRetry = null
  } = config;
  
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  let lastError = null;
  let lastResponse = null;
  
  for (let attempt = 1; attempt <= retries + 1; attempt++) {
    const controller = new AbortController();
    
    try {
      const fetchPromise = fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      });
      
      const timeoutPromise = createTimeoutPromise(timeout, controller);
      
      // Race between fetch and timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      lastResponse = response;
      
      // Check if response is ok
      if (response.ok) {
        return response;
      }
      
      // Check if we should retry
      if (attempt <= retries && isRetryableError(null, response)) {
        logApiError(endpoint, options.method || 'GET', new Error(`HTTP ${response.status}`), attempt);
        onRetry?.(attempt, response.status);
        await sleep(retryDelay * attempt); // Exponential backoff
        continue;
      }
      
      // Non-retryable error, return the response for handling
      return response;
      
    } catch (error) {
      lastError = error;
      
      // Check if we should retry
      if (attempt <= retries && isRetryableError(error, null)) {
        logApiError(endpoint, options.method || 'GET', error, attempt);
        onRetry?.(attempt, null);
        await sleep(retryDelay * attempt);
        continue;
      }
      
      // Final attempt failed or non-retryable error
      throw error;
    }
  }
  
  // All retries exhausted
  if (lastError) {
    throw lastError;
  }
  
  return lastResponse;
}

/**
 * GET request with timeout and retry
 */
export async function apiGet(endpoint, config = {}) {
  const response = await apiFetch(endpoint, { method: 'GET' }, config);
  return response.json();
}

/**
 * POST request with timeout and retry
 */
export async function apiPost(endpoint, data, config = {}) {
  const response = await apiFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(data)
  }, config);
  return response.json();
}

/**
 * PUT request with timeout and retry
 */
export async function apiPut(endpoint, data, config = {}) {
  const response = await apiFetch(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data)
  }, config);
  return response.json();
}

/**
 * PATCH request with timeout and retry
 */
export async function apiPatch(endpoint, data, config = {}) {
  const response = await apiFetch(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data)
  }, config);
  return response.json();
}

/**
 * DELETE request with timeout and retry
 */
export async function apiDelete(endpoint, config = {}) {
  const response = await apiFetch(endpoint, { method: 'DELETE' }, config);
  return response.json();
}

/**
 * Check if backend is available
 */
export async function checkBackendHealth() {
  try {
    const response = await apiFetch('/api/health', { method: 'GET' }, { 
      timeout: 5000, 
      retries: 1 
    });
    const data = await response.json();
    return data.status === 'ok' || data.status === 'degraded';
  } catch (error) {
    console.error('[Health Check] Backend unavailable:', error.message);
    return false;
  }
}

/**
 * API Error class for structured error handling
 */
export class ApiError extends Error {
  constructor(message, statusCode, endpoint, requestId = null) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.endpoint = endpoint;
    this.requestId = requestId;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Parse API error response
 */
export async function parseApiError(response, endpoint) {
  try {
    const data = await response.json();
    return new ApiError(
      data.error || data.message || 'Ett fel uppstod',
      response.status,
      endpoint,
      data.request_id
    );
  } catch {
    return new ApiError(
      'Ett fel uppstod vid kommunikation med servern',
      response.status,
      endpoint
    );
  }
}

/**
 * Check if response is a rate limit error
 */
export function isRateLimitError(response) {
  return response && response.status === 429;
}

/**
 * Parse rate limit error and get user-friendly message
 */
export async function parseRateLimitError(response) {
  try {
    const data = await response.json();
    return {
      isRateLimited: true,
      message: data.message || RATE_LIMIT_MESSAGES.default,
      retryAfter: data.retry_after || 60,
      error: data.error || 'rate_limit_exceeded'
    };
  } catch {
    return {
      isRateLimited: true,
      message: RATE_LIMIT_MESSAGES.default,
      retryAfter: 60,
      error: 'rate_limit_exceeded'
    };
  }
}

/**
 * Create a debounced function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, wait = 300) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Create a throttled function (rate limited)
 * @param {Function} func - Function to throttle
 * @param {number} limit - Minimum time between calls in milliseconds
 * @returns {Function} Throttled function
 */
export function throttle(func, limit = 1000) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Generate a form timestamp for timing validation
 * @returns {number} Current timestamp in milliseconds
 */
export function generateFormTimestamp() {
  return Date.now();
}

export default {
  apiFetch,
  apiGet,
  apiPost,
  apiPut,
  apiPatch,
  apiDelete,
  checkBackendHealth,
  ApiError,
  parseApiError,
  isRateLimitError,
  parseRateLimitError,
  debounce,
  throttle,
  generateFormTimestamp
};
